import type { ReactNode } from 'react'
import { ErrorBoundary } from 'react-error-boundary'

import { useExitWebView } from '@onl-web/shared/hooks'

import AlertModal from '@/components/AlertModal'
import { APP_REDIRECT_URL } from '@/constants'

import { reportError } from './reportError'

interface GlobalErrorBoundaryProps {
  children: ReactNode
}

const FALLBACK_TITLE = '일시적인 오류가 발생했어요'
const FALLBACK_DESCRIPTION = '앱을 다시 실행해주세요'

function GlobalErrorFallback() {
  const exitWebView = useExitWebView(APP_REDIRECT_URL)

  const handleConfirm = () => {
    try {
      exitWebView()
    } catch {
      window.location.href = APP_REDIRECT_URL
    }
  }

  return (
    <AlertModal
      isOpen
      title={FALLBACK_TITLE}
      description={FALLBACK_DESCRIPTION}
      onConfirm={handleConfirm}
    />
  )
}

export default function GlobalErrorBoundary({ children }: GlobalErrorBoundaryProps) {
  const handleError = (error: unknown, info: { componentStack?: string | null }) => {
    reportError(error, {
      source: 'render',
      level: 'fatal',
      componentStack: info.componentStack,
    })
  }

  return (
    <ErrorBoundary
      FallbackComponent={GlobalErrorFallback}
      onError={handleError}>
      {children}
    </ErrorBoundary>
  )
}
